import * as React from 'react';
import {FunctionComponent, useEffect, useMemo, useState} from 'react';
import {classNames} from '../../../../Support/classNames';
import {ReportData} from '../CompareReportPresenter';
import {averageCurve} from '../../../../../Strokes/averageCurve';
import {Curve} from '../../../../../Models/Curve';
import {CurveGraph, GraphableAverageCurve} from '../../../../Components/Charts/CurveGraph';
import {stringToColorHex} from '../Utils/stringToColorRgb';
import {strokesForSnippet} from '../Utils/strokesForSnippet';
import {tableColumnTitles} from '../Utils/TableColumnTitles';
import {calculateSplitTimes, SplitStrategy, Strategy} from '../../../../../Business/calculateSplitTimes';

interface OwnProps {
    splitStrategy: SplitStrategy;
    reportData: ReportData;
    className?: string;
}

export const AverageCurves: FunctionComponent<OwnProps> = ({splitStrategy, reportData, className}) => {
    const [activeCurveIndex, setActiveCurveIndex] = useState(0);
    const [normaliseCurves, setNormaliseCurves] = useState(false);
    const [highlightedSnippetId, setHighlightedSnippetId] = useState<number | null>(null);

    useEffect(() => {
        setActiveCurveIndex(0);
    }, [splitStrategy]);

    const snippets = [...reportData.snippets].sort((a, b) => {
        return a.publicWorkout.executedAt.getTime() - b.publicWorkout.executedAt.getTime();
    });

    const curves = useMemo(() => {
        return snippets.map((snippet) => {
            const downloadedIndoorWorkout = reportData.downloadedIndoorWorkouts.find((dIW) => {
                return dIW.id === snippet.publicWorkout.id;
            });
            if (!downloadedIndoorWorkout) {
                return {
                    snippet,
                    color: stringToColorHex('' + snippet.id),
                    averageCurves: null,
                };
            }
            const averageCurves = calculateSplitTimes(
                strokesForSnippet(snippet, downloadedIndoorWorkout),
                splitStrategy,
            ).map((split) =>
                averageCurve(
                    split.strokes.map((stroke) => stroke.curve).filter((curve): curve is Curve => curve !== null),
                ),
            );

            return {
                snippet,
                color: stringToColorHex('' + snippet.id),
                averageCurves,
            };
        });
    }, [splitStrategy, reportData]);

    const titles = tableColumnTitles(snippets);

    const maxCurves = Math.max(...curves.map((curve) => curve.averageCurves?.length || 0));

    return (
        <div className={classNames('card', className)}>
            <div className="card-title">
                <h5 className="d-flex justify-content-between align-items-baseline flex-row flex-wrap">
                    <span>Average curves</span>
                </h5>
            </div>

            <div className="card-body graphs">
                <div className="row">
                    <div className="col-8">
                        <CurveGraph
                            style={{
                                parent: {
                                    minWidth: 300,
                                    maxWidth: 500,
                                },
                            }}
                            normalise={normaliseCurves}
                            curves={curves
                                .map((snippetWithCurves): GraphableAverageCurve | null => {
                                    const curve = snippetWithCurves.averageCurves?.[activeCurveIndex];
                                    if (!curve) {
                                        return null;
                                    }
                                    return {
                                        curve: curve,
                                        color: snippetWithCurves.color,
                                        highlighted: highlightedSnippetId === snippetWithCurves.snippet.id,
                                    };
                                })
                                .filter(
                                    (graphableAverageCurve): graphableAverageCurve is GraphableAverageCurve =>
                                        graphableAverageCurve !== null,
                                )}
                        />
                    </div>
                    <div className="col-4">
                        <ul className="list-unstyled">
                            {curves.map((curve, index) => {
                                return (
                                    <li
                                        key={curve.snippet.id}
                                        className="d-flex justify-content-start align-content-center"
                                        onMouseEnter={() => setHighlightedSnippetId(curve.snippet.id)}
                                        onMouseLeave={() => setHighlightedSnippetId(null)}
                                    >
                                        <span
                                            style={{
                                                backgroundColor: curve.color,
                                                width: 10,
                                                height: 10,
                                                borderRadius: 10,
                                                margin: 8,
                                                display: 'block',
                                            }}
                                        />
                                        <span>
                                            {titles[index]} {curve.averageCurves === null ? ' (unavailable)' : null}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
                {maxCurves > 1 && (
                    <div className="row">
                        <div className="col-12">
                            <input
                                id="averageCurvesIndex"
                                type="range"
                                className="form-range"
                                value={activeCurveIndex + 1}
                                onChange={(event) => {
                                    setActiveCurveIndex(parseInt(event.target.value, 10) - 1);
                                }}
                                min={1}
                                max={maxCurves}
                                step={1}
                            />
                        </div>
                    </div>
                )}
                <div className="row mb-2 mt-2">
                    <div className="col-12">
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="normalisedCurvedCheckbox"
                                checked={normaliseCurves}
                                onChange={() => setNormaliseCurves((v) => !v)}
                            />
                            <label className="form-check-label" htmlFor="normalisedCurvedCheckbox">
                                Normalise curves
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
